// Confirmação de exclusão de negócio (plano §4.3.3) — espelho FIEL do
// ModelDeleteDialog (wireframe dlgDelGen): WfModalShell + header com lixeira
// vermelha + "Tem certeza que deseja excluir o negócio <nome>?" + .btn
// ghost/danger. Soft delete: DELETE /v1/businesses/:id marca deleted_at.
// Erros 403/409 mostram a MENSAGEM DO BACKEND no toast (§4.3.5).
import { useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api, type Business } from '@/lib/api';
import { WfModalShell } from '@/components/settings/WfModalShell';
import { useToast } from '@/components/ui/Toast';
import { deleteBusinessErrorMessage } from './business-constants';

interface BusinessDeleteDialogProps {
  business: Business | null;
  onClose: () => void;
  /** Pós-sucesso do caller: listagem recarrega (load), Configurações navega. */
  onDeleted: () => void;
}

export function BusinessDeleteDialog({ business, onClose, onDeleted }: BusinessDeleteDialogProps) {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (id: string) => api.deleteBusiness(id),
    onSuccess: () => {
      toast({ message: `Negócio "${business?.name}" excluído.`, type: 'success' });
      onClose();
      onDeleted();
    },
    onError: (err) => {
      toast({ message: deleteBusinessErrorMessage(err), type: 'error' });
    },
  });

  if (!business) return null;

  return (
    <WfModalShell
      open
      onClose={onClose}
      icon={<Trash2 className="ic" style={{ color: 'var(--wf-danger-text)' }} aria-hidden />}
      title="Confirmar exclusão"
      footer={
        <>
          <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn-danger"
            onClick={() => mutation.mutate(business.id)}
            disabled={mutation.isPending}
          >
            <Trash2 aria-hidden />
            {mutation.isPending ? 'Excluindo…' : 'Excluir'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '.92rem', lineHeight: 1.5, margin: 0 }}>
        Tem certeza que deseja excluir <b style={{ color: 'var(--wf-text2)' }}>o negócio</b>{' '}
        <b style={{ color: 'var(--wf-text2)' }}>"{business.name}"</b>?
      </p>
      <p style={{ fontSize: '.72rem', color: 'var(--wf-muted)', marginTop: 8 }}>
        Ele deixará de aparecer na plataforma.
      </p>
    </WfModalShell>
  );
}
